<template>
  <!-- Two columns -->
  <div class="row f-row col col-12 h-100" style="vertical-align: middle;">
    <!-- Row: title -->
    <div class="row f-row markets-chart-title d-flex align-items-center">
      <div class="col col-6" style="font-size: 1rem">
        <span style="color: var(--textactive)" v-if="isCurrency">
          {{ nameAssetSelected.slice(0, 3) }}/{{
            nameAssetSelected.slice(3, 6)
          }}
        </span>
        <span style="color: var(--textactive)" v-else>
          {{ nameAssetSelected }}
        </span>
        Forecast Analysis
      </div>
      <slot name="spot"></slot>
    </div>
    <!-- First column -->
    <section class="col col-5 container__section-first h-100">
      <!-- Spinner -->
      <div
        v-if="showLoadingAll || showLoadingHistorical"
        class="text-center"
        style="width: 100%; top: calc(33vh - 8px)!important; position: absolute;"
      >
        <QSLoading
          :isLoading="showLoadingAll || showLoadingHistorical"
          :size="35"
          :colorTheme="colorTheme"
        />
      </div>
      <!-- No data available -->
      <div
        v-else-if="dataForecastAll"
        class="text-center"
        style="width: 100%; top: calc(50% - 8px)!important; position: absolute;"
      >
        No Data Available
      </div>
      <!-- Asset selector -->
      <template v-else>
        <div class="row f-row container__asset-selector" style="height: 60%;">
          <asset-selector
            :loadingAll="showLoadingAll"
            :currencyList="data.currencyList"
            :commodityList="data.commodityList"
            :indexList="data.indexList"
            :commodityDefs="nameAssetSelected"
            @onAssetsSelectedModel="onAssetsSelectedModel"
            @onShowForecastAssetSelected="onShowForecastAssetSelected"
          />
        </div>
        <!-- Chart forecast comparison -->
        <comparison-forecast
          :dataHistorical="data.historical"
          :chartDataRaw="chartDataRawFilter"
          :colorTheme="colorTheme"
          :currencyString="currencyString"
          :isCurrency="isCurrency"
          :processData="processData"
          :showLoadingAll="showLoadingAll"
          :showLoadingHistorical="showLoading"
          :comparedAssetLong="userSelections.comparedAssetLong"
          :commodityName="commodityName"
          :dataForecast="data.forecast"
          :commodityDefs="nameAssetSelected"
        />
      </template>
    </section>
    <!-- Second column -->
    <section class="col col-7 h-100">
      <!-- Chart model prediction -->
      <div class="row f-row" style="height: 75%;">
        <!-- Skeleton chart -->
        <div
          v-if="showLoadingModel"
          class="text-center"
          style="width: 100%; height: 100%;"
        >
          <Chart
            :processData="processData"
            :colorTheme="colorTheme"
            :isRunModel="userSelectionsRunModel"
            @runModel="runModel"
          />
        </div>
        <!-- No data available -->
        <div
          v-else-if="dataForecastAll"
          class="text-center"
          style="width: 100%; top: calc(50% - 8px)!important; position: absolute;"
        >
          No Data Available
        </div>
        <ModelForecast
          v-else
          :chartDataRaw="chartDataRawFilter"
          :dataForecast="data.forecast"
          :dataModel="data.model"
          :colorTheme="colorTheme"
          :processData="processData"
          :showLoadingAll="showLoadingAll"
          :showLoadingHistorical="showLoadingHistorical"
          :comparedAssetLong="userSelections.comparedAssetLong"
          :commodityDefs="nameAssetSelected"
        />
      </div>
      <!-- Table model prediction -->
      <div class="row f-row container__table--model" style="height: 25%;">
        <div class="col col-12 text-center">
          <div
            v-if="showLoadingAll || showLoadingHistorical"
            class="text-center"
            style="width: 100%; top: calc(10% - 8px)!important; position: absolute;"
          >
            <QSLoading
              :isLoading="showLoadingAll || showLoadingHistorical"
              :size="35"
              :colorTheme="colorTheme"
            />
          </div>
          <!-- No data available -->
          <div
            v-else-if="dataForecastAll"
            class="text-center"
            style="width: 100%; top: calc(10% - 8px)!important; position: absolute;"
          >
            No Data Available
          </div>
          <Table
            v-else
            :commodityDefs="nameAssetSelected"
            :colorTheme="colorTheme"
            :loadingModel="showLoadingModel"
            :dataTable="data.table"
            :isRunModel="userSelectionsRunModel"
            :isCurrency="isCurrency"
          />
        </div>
      </div>
    </section>
  </div>
</template>

<script>
import { getCoIntegrationForecast } from "@/_core/data/api_calls/getCointegrationForecast";
import { getCoIntegrationHistorical } from "@/_core/data/api_calls/getCointegrationHistorical";
import { getComdtyForecast } from "@/_core/data/api_calls/getCommodityForecasts";
import { getCurncyForecast } from "@/_core/data/api_calls/getCurrencyForecasts";
import { getIndexForecast } from "@/_core/data/api_calls/getIndexForecasts";
import { lengthByDate } from "@/_core/_helpers/_helpers";
import AssetSelector from "./ForecastAnalysis/AssetSelector.vue";
import ComparisonForecast from "./ForecastAnalysis/ComparisonForecast.vue";
import Chart from "../skeletons/ChartBar.vue";
import ModelForecast from "./ForecastAnalysis/ModelForecast.vue";
import Table from "./ForecastAnalysis/Table.vue";
import QSLoading from "@/components/QSLoading.vue";

export default {
  components: {
    QSLoading,
    AssetSelector,
    ComparisonForecast,
    Chart,
    ModelForecast,
    Table,
  },
  name: "ForecastAnalysis",
  props: {
    chartDataRaw: { required: true },
    colorTheme: { required: true, type: Boolean, default: true },
    commodityDefs: {
      required: true,
      default: () => [
        { pk_idcommodity: 0, fk_idproduct: "N.A", selected_contract: 0 },
      ],
    },
    dataReady: { required: true },
    processData: { required: true },
    isCurrency: { required: true },
    selectedCurrency: { required: true, default: "na" },
    commodityStringLong: { required: false, default: "N.A" },
    commodityName: { required: false, default: "N.A" },
    currencyString: { required: true, default: () => [] },
  },
  data() {
    return {
      data: {
        commodityByPk: { 2: "BRENT" },
        commodityList: [{ pk: 2, name: "BRENT" }],
        currencyByPk: { 2: "EURUSD" },
        currencyList: [{ pk: 2, name: "EURUSD" }],
        forecast: [],
        forecastCommodities: [],
        forecastCurrencies: [],
        forecastIndexes: [],
        historical: [],
        indexByPk: { 35: "S&P GSCI" },
        indexList: [{ pk: 35, name: "S&P GSCI" }],
        model: [],
        table: {},
      },
      loading: {
        forecastCommodities: false,
        forecastCurrencies: false,
        forecastIndexes: false,
        historical: false,
        model: false,
      },
      settings: {
        initialDate: new Date(
          new Date().setFullYear(new Date().getFullYear() - 1),
        ),
        pickedDateInterval: "1Y",
      },
      userSelections: {
        commodities: [],
        currencies: [],
        indexes: [],
        comparedAssetLong: "",
        changeDataAssets: false,
        runModel: false,
      },
    };
  },
  created() {
    getComdtyForecast(this);
    getCurncyForecast(this);
    getIndexForecast(this);
    this.loading["model"] = true;
  },
  computed: {
    showLoadingAll() {
      return (
        this.loading["forecastCommodities"] ||
        this.loading["forecastCurrencies"] ||
        this.loading["forecastIndexes"]
      );
    },
    showLoadingHistorical() {
      return this.loading["historical"];
    },
    showLoading() {
      return this.chartDataRaw.length === 0;
    },
    showLoadingModel() {
      return this.loading["model"];
    },
    userSelectionsRunModel() {
      return this.userSelections.runModel;
    },
    dataForecastAll() {
      const data = this.data;
      return (
        data.forecastCommodities.length === 0 &&
        data.forecastCurrencies.length === 0 &&
        data.forecastIndexes.length === 0
      );
    },
    nameAssetSelected() {
      return this.isCurrency
        ? this.currencyString.currency_name
        : this.commodityDefs.commodity_name;
    },
    chartDataRawFilter() {
      if (this.chartDataRaw.length > 0) {
        this.getDataTable();
      }
      return this.chartDataRaw.slice(-this.selectedDays());
    },
    userSelctAssets() {
      return (
        this.userSelections.commodities.length === 0 &&
        this.userSelections.currencies.length === 0 &&
        this.userSelections.indexes.length === 0
      );
    },
  },
  watch: {
    showLoadingAll(val) {
      if (val === false) {
        getCoIntegrationHistorical(
          this,
          this.data["commodityList"].map((d) => d.pk),
          this.data["currencyList"].map((d) => d.pk),
          this.data["indexList"].map((d) => d.pk),
        );
        this.joinForecasts();
      }
    },
    showLoadingModel(val) {
      if (!val) {
        this.dataTable;
      }
    },
    dataForecastAll() {
      const data = this.data;
      return (
        data.forecastCommodities.length === 0 &&
        data.forecastCurrencies.length === 0 &&
        data.forecastIndexes.length === 0
      );
    },
    "data.model": "getDataTable",
    "userSelections.changeDataAssets": "handleDataAssets",
    "userSelections.comparedAssetLong": "handledCompared",
    commodityDefs() {
      this.nameAssetSelected;
    },
  },
  methods: {
    joinForecasts() {
      Object.assign(this.data, {
        forecast: this.data["forecastCommodities"]
          .concat(this.data["forecastCurrencies"])
          .concat(this.data["forecastIndexes"]),
      });
    },
    selectedDays() {
      return lengthByDate(
        this.settings.pickedDateInterval,
        this.chartDataRaw.map((d) => d.date),
      );
    },
    parseAndForecast() {
      if (this.chartDataRaw.length === 0) {
        return;
      }
      let _this = this;
      let xAssets = [];
      if (this.userSelections["commodities"].length > 0) {
        xAssets = xAssets.concat(
          this.userSelections["commodities"].map(
            (d) => _this.data["commodityByPk"][d],
          ),
        );
      }
      if (this.userSelections["currencies"].length > 0) {
        xAssets = xAssets.concat(
          this.userSelections["currencies"].map(
            (d) => _this.data["currencyByPk"][d],
          ),
        );
      }
      if (this.userSelections["indexes"].length > 0) {
        xAssets = xAssets.concat(
          this.userSelections["indexes"].map((d) => _this.data["indexByPk"][d]),
        );
      }
      let yStr = this.nameAssetSelected;
      let yHist = this.chartDataRaw
        .filter((d) => +d.date >= +_this.settings["initialDate"])
        .map(function(d) {
          let obj = { date: d.date };
          obj[yStr] = d.close;
          return obj;
        });
      xAssets = ["date"].concat(xAssets);
      let xHist = this.data["historical"].map(function(d) {
        return Object.keys(d)
          .filter((key) => xAssets.includes(key))
          .reduce((obj, key) => {
            obj[key] = d[key];
            return obj;
          }, {});
      });
      let xFcst = this.data["forecast"].filter((d) => xAssets.includes(d.name));

      getCoIntegrationForecast(this, yStr, yHist, xAssets, xHist, xFcst);
    },
    onAssetsSelectedModel(dataAssets) {
      const userSelct = this.userSelections;
      dataAssets.forEach((d) => {
        const name = d.name.toLowerCase();
        if (d.data.length === 0) {
          userSelct[name] = [];
        } else {
          d.data.forEach((data) => {
            const pk = data.pk;
            // add asset
            if (!userSelct[name].includes(pk)) {
              userSelct[name].push(pk);
            } else {
              // verify assets deleted
              userSelct[name] = userSelct[name].filter((pKey) => pKey === pk);
            }
          });
        }
      });
      if (dataAssets.length === 0) {
        userSelct.currencies = [];
        userSelct.commodities = [];
        userSelct.indexes = [];
      }
      userSelct.changeDataAssets = true;
    },
    // asset selected VS asset radio button
    onShowForecastAssetSelected(asset) {
      this.userSelections.comparedAssetLong = asset;
    },
    runModel() {
      if (!this.userSelctAssets) {
        this.data.model = [];
        this.userSelections.changeDataAssets = false;
        this.userSelections.runModel = true;
        this.parseAndForecast();
        this.getDataTable();
      } else {
        this.userSelections.runModel = false;
      }
    },
    getDataTable() {
      const uniqueDateFcst = this.data.forecast.reduce((unique, item) => {
        const dateInMilliseconds = +item.date;
        if (!unique.some((date) => +date === dateInMilliseconds)) {
          unique.push(new Date(dateInMilliseconds));
        }
        return unique;
      }, []);
      const dateHeaders = uniqueDateFcst.map((obj) => ({ date: obj }));
      const dataForecastFilter = this.data.forecast.filter(
        (d) => d.name === this.nameAssetSelected,
      );
      const close = this.chartDataRaw.at(-1).close;
      let dataModelFilter = [];

      if (this.userSelectionsRunModel) {
        dataModelFilter = uniqueDateFcst
          .map((forecastDate) => {
            const closestModelDate = this.data.model.reduce(
              (closest, modelItem) => {
                const modelDate = +modelItem.date;
                const closestDate = closest ? +closest.date : null;
                const forecastDateMs = +forecastDate;
                // verify earliest date
                if (
                  Math.abs(modelDate - forecastDateMs) <
                    Math.abs(closestDate - forecastDateMs) ||
                  !closest
                ) {
                  return modelItem;
                }
                return closest;
              },
              null,
            );
            return closestModelDate;
          })
          .filter((item) => item !== null);

        dataModelFilter.sort((a, b) => +a.date - +b.date);
      }
      dataForecastFilter.sort((a, b) => +a.date - +b.date);
      dataForecastFilter.unshift({ close });
      dataModelFilter.unshift({ close });

      this.data.table = {
        dateHeaders,
        assetSelected: dataForecastFilter,
        model: dataModelFilter,
      };
    },
    handledCompared() {},
    handleDataAssets() {
      if (this.userSelections.changeDataAssets) {
        this.userSelections.runModel = false;
      } else this.userSelections.runModel = true;
      this.userSelctAssets;
      this.loading["model"] = true;
    },
  },
};
</script>

<style scoped>
.no_nav_title_height {
  height: calc(100vh - 67px);
}
.container__section-first {
  border-right: 0.5px solid var(--graylightbackground-alpha4);
}
@media (min-width: 1400px) {
  .container__table--model {
    margin-top: 15px;
  }
}
</style>
